import { useCallback, type KeyboardEvent } from 'react';
import {
  addDays,
  addMonths,
  isoDate,
  parseISODate,
  todayPlain,
} from '../utils/dateUtils.js';

export interface CalendarKeyboardOptions {
  /** Currently focused / selected day (`YYYY-MM-DD`). */
  readonly selected: string;
  /** Currently visible cursor (`YYYY-MM-DD`) — month anchor in MonthView. */
  readonly cursor: string;
  readonly onSelectedChange: (next: string) => void;
  readonly onCursorChange?: ((next: string) => void) | undefined;
  /** Fired on Enter / Space when a day is focused. */
  readonly onActivate?: ((dateKey: string) => void) | undefined;
  /** Number of columns for the focusable grid (7 for a month / week
   *  view, 1 for an agenda list). */
  readonly columns?: number | undefined;
  /** Optional viewer TZ so "Today" jumps land on the right local day. */
  readonly viewerTz?: string | undefined;
}

export interface CalendarKeyboardBinding {
  readonly onKeyDown: (e: KeyboardEvent<HTMLElement>) => void;
}

/**
 * Implements plan.md XIX.2 keyboard nav for grid-shaped calendar views.
 *
 * - ArrowLeft  / ArrowRight : ±1 day
 * - ArrowUp    / ArrowDown  : ±`columns` days (one row)
 * - PageUp     / PageDown   : ±1 month (or ±7 days when `columns` is 1)
 * - Home / End              : start / end of the current row (when grid)
 * - `t`                     : jump to today
 * - `,` / `.`               : ±1 month (cursor only)
 * - Enter / Space           : activate the selected day
 *
 * The hook does NOT manage focus itself — callers are expected to have a
 * roving `tabIndex={isSelected ? 0 : -1}` pattern and to focus the
 * matching cell after `onSelectedChange`.
 */
export function useCalendarKeyboard(
  options: CalendarKeyboardOptions,
): CalendarKeyboardBinding {
  const {
    selected,
    cursor,
    onSelectedChange,
    onCursorChange,
    onActivate,
    columns = 7,
    viewerTz,
  } = options;

  const handler = useCallback(
    (e: KeyboardEvent<HTMLElement>) => {
      const sel = parseISODate(selected);
      const cur = parseISODate(cursor);
      const advance = (days: number): void => {
        const next = addDays(sel, days);
        const nextKey = isoDate(next);
        onSelectedChange(nextKey);
        // If we crossed a month boundary in either direction, scroll the
        // cursor to follow.
        if (next.year !== cur.year || next.month !== cur.month) {
          onCursorChange?.(isoDate({ year: next.year, month: next.month, day: 1 }));
        }
      };
      const advanceMonth = (n: number): void => {
        const nextCur = addMonths(cur, n);
        onCursorChange?.(isoDate(nextCur));
        // Try to keep the same day-of-month; fall back to last day.
        const dim = new Date(nextCur.year, nextCur.month, 0).getDate();
        onSelectedChange(
          isoDate({
            year: nextCur.year,
            month: nextCur.month,
            day: Math.min(sel.day, dim),
          }),
        );
      };

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          advance(-1);
          break;
        case 'ArrowRight':
          e.preventDefault();
          advance(1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          advance(-columns);
          break;
        case 'ArrowDown':
          e.preventDefault();
          advance(columns);
          break;
        case 'Home':
          e.preventDefault();
          // Move to the start of the current row (first column in the
          // grid). For a 7-col month grid this is "previous Sunday or
          // weekStart" relative to the focused cell.
          if (columns > 1) {
            const dow = new Date(sel.year, sel.month - 1, sel.day).getDay();
            advance(-dow);
          }
          break;
        case 'End':
          e.preventDefault();
          if (columns > 1) {
            const dow = new Date(sel.year, sel.month - 1, sel.day).getDay();
            advance(6 - dow);
          }
          break;
        case 'PageUp':
          e.preventDefault();
          if (columns >= 7) advanceMonth(-1);
          else advance(-7);
          break;
        case 'PageDown':
          e.preventDefault();
          if (columns >= 7) advanceMonth(1);
          else advance(7);
          break;
        case ',':
          e.preventDefault();
          advanceMonth(-1);
          break;
        case '.':
          e.preventDefault();
          advanceMonth(1);
          break;
        case 't':
        case 'T': {
          e.preventDefault();
          const t = todayPlain(viewerTz);
          onCursorChange?.(isoDate({ year: t.year, month: t.month, day: 1 }));
          onSelectedChange(isoDate(t));
          break;
        }
        case 'Enter':
        case ' ':
          e.preventDefault();
          onActivate?.(selected);
          break;
        default:
      }
    },
    [selected, cursor, onSelectedChange, onCursorChange, onActivate, columns, viewerTz],
  );

  return { onKeyDown: handler };
}
